import ReactDOMServer from 'react-dom/server';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useSelector } from 'react-redux';
import { Tooltip } from 'react-tooltip';
import Loader from './Loader';
import useResizeObserver from 'utils/useResizeObserver';
import { useEffect } from 'react';

const layoutCalibration = {
  top: 14.5,
  left: 0,
  lineHeight: 10
};

const AutocompleteInline = ({ onLinesCountChange }) => {
  /* Selectors */
  const isOpen = useSelector((state) => state.autocomplete.isOpen);
  
  const displayText = useSelector((state) => {
    if (state.autocomplete.containerData.hasTextAfterCursor) {
      return state.autocomplete.nextwords;
    }

    return state.autocomplete.textSuggestion;
  });
  
  const isLoading = useSelector((state) => state.autocomplete.isLoading);
  const streamType = useSelector((state) => state.autocomplete.streamType);
  const containerData = useSelector((state) => state.autocomplete.containerData);

  const hasTextAfterCursor = containerData.hasTextAfterCursor;
  const width = containerData.left ? containerData.left - layoutCalibration.left : 0;

  const hintContent = ReactDOMServer.renderToStaticMarkup(
    <>
      <div>Press "Tab" to autocomplete</div>
      <div>Press 2x "Tab" to complete sentence</div>
      <div>Type "+++" to continue writing</div>
    </>
  );

  const [ref, size] = useResizeObserver(50);

  useEffect(() => {
    if (ref.current && size.height) {
      // Get the line height of the element
      const lineHeight = parseInt(window.getComputedStyle(ref.current).lineHeight);
      // // Calculate the number of lines
      const lineCount = Math.round(size.height / lineHeight);

      onLinesCountChange(lineCount);
    }
  }, [size, ref, onLinesCountChange]);

  return (
    <>
      <Tooltip id='autocomplete-tab' />
      <AutocompleteWrapper
        isOpen={isOpen}
        isPopup={hasTextAfterCursor}
        bounds={containerData}
        calibrationTop={layoutCalibration.top}
      >
        <Text
          ref={ref}
          isPopup={hasTextAfterCursor}
          width={width}
          lineHeight={containerData.height + layoutCalibration.lineHeight}
        >
          <Indent width={width} />

          {!!displayText && displayText}

          {isLoading && (
            <>
              {`completing ${streamType} `}
              <Loader />
            </>
          )}

          {!isLoading && (!!displayText) && (
            <Hint
              data-tooltip-id='autocomplete-tab'
              data-tooltip-html={hintContent}>
              tab
            </Hint>
          )}
        </Text>
      </AutocompleteWrapper>
    </>
  );
}

export default AutocompleteInline;

AutocompleteInline.propTypes = {
  onLinesCountChange: PropTypes.func,
};

AutocompleteInline.defaultProps = {
  onLinesCountChange: () => {}
}

const AutocompleteWrapper = styled.div`
  position: absolute;
  left: 0;
  padding: 12px 0;
  color: var(--lizzy-black-35);
  width: 100%;
  display: ${p => p.isOpen ? 'block' : 'none'};
  top: ${p => p.bounds.top - p.calibrationTop}px;
`;

const Indent = styled.div`
  position: relative;
  display: inline-block;

  ${p => p.width && css`
    width: ${p.width}px;
  `};
`;

const Text = styled.div`
  position: relative;
  display: inline-block;
  z-index: 12;
  border-radius: 8px;
  line-height: 1.42;
/* 
  clip-path: polygon(
    ${p => p.width}px ${p => p.lineHeight}px,
    ${p => p.width}px 0,
    100% 0,
    100% 100%,
    0 100%,
    0 ${p => p.lineHeight}px
  ); */
`;

const Hint = styled.span`
  display: inline-block;
  line-height: 13px;
  border: 1px solid var(--lizzy-black-35);
  font-size: 10px;
  border-radius: 4px;
  padding: 2px 7px;
  margin-left: 7px;
  transform: translateY(-2px);
  position: relative;
  z-index: 10;
  cursor: default;
`;